import pako from 'pako';
import {
  IConfiguration,
  IConfigurationContentData,
} from 'src/types/configuration';

import { IDecodedCfgFile } from './types';

const parseContainer = (text: string): IConfiguration<string> => {
  const parsed = JSON.parse(text) as IConfiguration<string>;
  if (!parsed['roqs-container']?.['content-list']?.length) {
    throw new Error('Invalid configuration file');
  }
  return parsed;
};

const base64ToBytes = (value: string): Uint8Array => {
  if (typeof atob === 'function') {
    const binary = atob(value);
    return Uint8Array.from(binary, (char) => char.charCodeAt(0));
  }
  if (typeof Buffer !== 'undefined') {
    return new Uint8Array(Buffer.from(value, 'base64'));
  }
  throw new Error('Base64 decoder is not available');
};

const inflateContent = (contentData: string): string => {
  const bytes = base64ToBytes(contentData);

  try {
    return pako.ungzip(bytes, { to: 'string' });
  } catch (gzipError) {
    try {
      return pako.inflateRaw(bytes.slice(10, -8), { to: 'string' });
    } catch (inflateError) {
      const message =
        gzipError instanceof Error ? gzipError.message : 'Unknown gzip error';
      const rawMessage =
        inflateError instanceof Error
          ? inflateError.message
          : 'Unknown inflate error';
      throw new Error(
        `Unable to decode configuration payload (${message}; ${rawMessage})`,
      );
    }
  }
};

export const decodeCfgText = ({
  id,
  fileName,
  text,
}: {
  id: string;
  fileName: string;
  text: string;
}): IDecodedCfgFile<IConfigurationContentData> => {
  const container = parseContainer(text);
  const contentItems = container['roqs-container']['content-list'].map(
    (item) => ({
      ...item,
      'content-data': JSON.parse(inflateContent(item['content-data'])),
    }),
  );

  return {
    id,
    fileName,
    devicesCounts: contentItems.length,
    data: {
      ...container,
      'roqs-container': {
        ...container['roqs-container'],
        'content-list': contentItems,
      },
    },
  };
};

export const decodeCfgFile = async (
  file: File,
): Promise<IDecodedCfgFile<IConfigurationContentData>> => {
  const text = await file.text();
  return decodeCfgText({
    id: `${file.name}-${file.lastModified}`,
    fileName: file.name,
    text,
  });
};
